/**
 * V2 Layout — Instagram/Snapchat-style pager shell.
 *
 * Architecture:
 * - All 3 pages rendered side-by-side in a horizontal strip
 * - Native touch events drive a MotionValue for live finger-tracking
 * - Direction lock: horizontal → drag pages, vertical → native scroll
 * - Spring snap on release, no animation delay between tabs
 */
import { useState, useRef, useCallback, lazy, Suspense, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NowPlaying } from '@/components/roxbox/NowPlaying';
import { GlobalMiniPlayer } from '@/components/roxbox/GlobalMiniPlayer';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaBookOpenReader } from 'react-icons/fa6';
import { BsMusicPlayerFill } from 'react-icons/bs';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';
import { cssSnapTo } from '@/constants/animation';
import { cn, maskEmail } from '@/lib/utils';
import { hapticMedium } from '@/lib/haptics';
import { BookLoader } from '@/components/ui/book-loader';
import { useAuth } from '@/components/AuthProvider';
import { useStableAvatar } from '@/hooks/use-stable-avatar';
import { useProfileHierarchy } from '@/hooks/use-profile-hierarchy';
import { useAppFlow } from '@/contexts/AppFlowContext';
import { V2ProfileSheet } from './V2ProfileSheet';
import { signOutCompletely } from '@/utils/authCleanup';
import { useToast } from '@/hooks/use-toast';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { useBuilderCatalog } from '@/hooks/use-builder-catalog';
import { useV2ReadingJourney } from '@/hooks/use-v2-reading-journey';
import { useRoxboxPlayerContext } from '@/contexts/RoxboxPlayerContext';
import { Loader2 } from 'lucide-react';
import { V2LoadingScreen } from './V2LoadingScreen';
import { useV2TabMachine, type TabId } from '@/hooks/use-v2-tab-machine';
import { useV2SwipeGesture } from '@/hooks/use-v2-swipe-gesture';

const TAB_IDS: TabId[] = ['buxbox', 'mybux', 'roxbox'];

const SmartPaywall = lazy(() => import('@/components/subscription/SmartPaywall'));
const V2Home = lazy(() => import('@/pages/v2/V2Home'));
const V2MyBux = lazy(() => import('@/pages/v2/V2MyBux'));
const V2RoxBox = lazy(() => import('@/pages/v2/V2RoxBox'));

function HomeIcon({ active }: { active: boolean }) {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 10.5L12 3L21 10.5V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V10.5Z"
        fill={active ? 'currentColor' : 'currentColor'}
        stroke="currentColor"
        strokeWidth={0}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LibraryIcon({ active }: { active: boolean }) {
  return <FaBookOpenReader size={22} style={{ opacity: active ? 1 : 0.9 }} />;
}

function MusicIcon({ active }: { active: boolean }) {
  return <BsMusicPlayerFill size={22} style={{ opacity: active ? 1 : 0.9 }} />;
}

const TAB_META = [
  { id: 'buxbox' as const, label: 'BuxBox', Icon: HomeIcon },
  { id: 'mybux' as const, label: 'ForYou', Icon: LibraryIcon },
  { id: 'roxbox' as const, label: 'JukeBox', Icon: MusicIcon },
];

const TAB_COMPONENTS: Record<TabId, React.LazyExoticComponent<React.ComponentType<any>>> = {
  buxbox: V2Home,
  mybux: V2MyBux,
  roxbox: V2RoxBox,
};

function TabFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <BookLoader />
    </div>
  );
}

// CSS transitions replace framer-motion animate()

export function V2Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const { currentProfile } = useStableAvatar();
  const { activeProfile } = useProfileHierarchy();
  const { showProfiles } = useAppFlow();
  const { toast } = useToast();
  const { data: subscriptionStatus, isLoading: isSubLoading } = useSubscriptionStatus();

  const {
    isNowPlayingOpen, setNowPlayingOpen,
    currentTrack, currentAudiobookPage,
    isPlaying, currentTime, duration, isLooping,
    togglePlay, previousTrack, nextTrack, seek, toggleLoop,
  } = useRoxboxPlayerContext();

  useBuilderCatalog();
  useV2ReadingJourney();

  /* ─── Tab state ─── */
  const { activeTab, activeIndex, requestTab, setTabByIndex } = useV2TabMachine('buxbox');

  /* ─── Pager strip ─── */
  const containerRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const skipNextSnapRef = useRef(false);

  // Animate x when tab changes via click (skip if swipe already handled the snap)
  useEffect(() => {
    const w = containerRef.current?.offsetWidth || 0;
    if (w === 0) return;
    const target = -activeIndex * w;
    if (skipNextSnapRef.current) {
      skipNextSnapRef.current = false;
      return;
    }
    if (Math.abs(x.get() - target) < 2) return;
    cssSnapTo(x, target, stripRef.current);
  }, [activeIndex, x]);

  // Re-snap on resize
  useEffect(() => {
    const handler = () => {
      const w = containerRef.current?.offsetWidth || 0;
      if (w > 0) x.set(-activeIndex * w);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [activeIndex, x]);

  /* ─── Swipe gesture ─── */
  useV2SwipeGesture({
    containerRef,
    stripRef,
    x,
    activeIndex,
    pageCount: TAB_IDS.length,
    onPageChange: (index) => {
      skipNextSnapRef.current = true;
      setTabByIndex(index);
    },
    disabled: isNowPlayingOpen,
  });

  /* ─── UI state ─── */
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const forYouTabRef = useRef<HTMLButtonElement>(null);
  const navBarRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [bubbleStyle, setBubbleStyle] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  /* ─── Bubble position ─── */
  const updateBubble = useCallback(() => {
    const bar = navBarRef.current;
    const btn = tabRefs.current[activeIndex];
    if (!bar || !btn) return;
    const barRect = bar.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const bubbleW = btnRect.width + 4;
    const left = btnRect.left - barRect.left + (btnRect.width - bubbleW) / 2;
    setBubbleStyle({ left, width: bubbleW });
  }, [activeIndex]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(updateBubble);
    });
    window.addEventListener('resize', updateBubble);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateBubble);
    };
  }, [updateBubble]);

  /* ─── ForYou shake animation on mylist-added ─── */
  useEffect(() => {
    const handler = () => {
      const el = forYouTabRef.current;
      if (!el) return;
      el.classList.remove('bux-shake');
      void el.offsetWidth;
      el.classList.add('bux-shake');
      el.style.color = '#FFD60A';
      const label = el.querySelector('span');
      if (label) label.style.color = '#FFD60A';
      hapticMedium();
      setTimeout(() => {
        el.classList.remove('bux-shake');
        el.style.color = '';
        if (label) label.style.color = '';
      }, 1200);
    };
    window.addEventListener('bux:mylist-added', handler);
    return () => window.removeEventListener('bux:mylist-added', handler);
  }, []);

  const avatarUrl = currentProfile?.avatar_url;
  const displayName = currentProfile?.name || session?.user?.email?.split('@')[0] || 'User';
  const email = maskEmail(session?.user?.email || '');
  const isKidsProfile = activeProfile?.profile_type === 'kids';

  const handleLogout = useCallback(async () => {
    try {
      await signOutCompletely(navigate, '/v2/iosflow');
    } catch (error: any) {
      toast({ title: 'Error signing out', description: error.message, variant: 'destructive' });
    }
  }, [navigate, toast]);

  const handleSwitchProfiles = useCallback(() => {
    showProfiles();
  }, [showProfiles]);

  /* ─── Scroll restoration ─── */
  useEffect(() => {
    const state = location.state as { v2ScrollTop?: number } | null;
    if (activeTab !== 'buxbox' || typeof state?.v2ScrollTop !== 'number') return;
    const el = containerRef.current?.querySelector('[data-v2-page="buxbox"]') as HTMLElement | null;
    if (el) requestAnimationFrame(() => { el.scrollTop = state.v2ScrollTop!; });
  }, [activeTab, location.key, location.state]);

  /* ─── Subscription gate ─── */
  useEffect(() => {
    if (!isSubLoading && subscriptionStatus && !subscriptionStatus.subscribed) {
      setPaywallOpen(true);
    }
  }, [isSubLoading, subscriptionStatus]);

  if (isSubLoading) {
    return <V2LoadingScreen />;
  }

  if (!subscriptionStatus?.subscribed) {
    return (
      <div className="fixed inset-0 z-50 bg-background">
        <Suspense fallback={<V2LoadingScreen />}>
          <SmartPaywall
            isOpen={paywallOpen}
            onClose={async () => {
              setPaywallOpen(false);
              await signOutCompletely(navigate, '/v2/iosflow');
            }}
            onPurchaseComplete={() => setPaywallOpen(false)}
          />
          {!paywallOpen && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <p className="text-muted-foreground text-sm">A subscription is required to access BuxBox.</p>
              <button
                onClick={() => setPaywallOpen(true)}
                className="px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold"
              >
                View Plans
              </button>
            </div>
          )}
        </Suspense>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col overflow-hidden">
      {/* ─── Status bar gradient ─── */}
      <div
        className="absolute top-0 left-0 right-0 z-40 pointer-events-none"
        style={{
          height: 'calc(env(safe-area-inset-top, 44px) + 3rem)',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)',
        }}
      />

      {/* ─── Pager: all 3 pages side-by-side ─── */}
      <div ref={containerRef} className="flex-1 relative overflow-hidden">
        <motion.div
          ref={stripRef}
          className="flex h-full will-change-transform"
          style={{ x, width: '300%' }}
        >
          {TAB_IDS.map((id) => {
            const Comp = TAB_COMPONENTS[id];
            return (
              <div
                key={id}
                data-v2-page={id}
                data-v2-scroll-container={id === 'buxbox' ? 'true' : undefined}
                className="w-1/3 h-full overflow-y-auto overflow-x-hidden"
              >
                <Suspense fallback={<TabFallback />}>
                  <Comp />
                </Suspense>
              </div>
            );
          })}
        </motion.div>
      </div>

      {/* ─── Footer: Mini player + Nav bar + Avatar ─── */}
      <div
        data-no-tab-swipe="true"
        className="absolute bottom-0 left-0 right-0 z-50 px-4"
        style={{
          paddingBottom: 'calc(max(env(safe-area-inset-bottom), 8px) + 10px)',
          pointerEvents: 'none',
        }}
      >
        <div className="mx-auto flex flex-col items-stretch gap-3 w-fit max-w-full">
          <div className="min-w-0">
            <GlobalMiniPlayer embeddedInV2Footer />
          </div>

          <div className="flex items-center gap-x-2.5">

          {/* Nav pill */}
          <div
            ref={navBarRef}
            className="relative flex items-center justify-center gap-0 px-2 py-1.5 rounded-full overflow-hidden"
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              boxShadow: '0 6px 6px rgba(0, 0, 0, 0.2), 0 0 20px rgba(0, 0, 0, 0.1)',
              border: '0.5px solid rgba(255, 255, 255, 0.2)',
              pointerEvents: 'auto',
            }}
          >
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                zIndex: 0,
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
              }}
            />

            {bubbleStyle.width > 0 && (
              <div
                className="absolute rounded-full pointer-events-none"
                style={{
                  width: `${bubbleStyle.width}px`,
                  height: '3.2rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  left: `${bubbleStyle.left}px`,
                  zIndex: 3,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  boxShadow: `
                    0 3px 8px rgba(0,0,0,0.3),
                    inset 0 1px 0 rgba(255,255,255,0.15),
                    inset 0 -0.5px 0 rgba(255,255,255,0.05)
                  `,
                  transition: 'left 0.5s cubic-bezier(0.22, 1.2, 0.36, 1)',
                  overflow: 'hidden',
                }}
              />
            )}

            {TAB_META.map(({ id, label, Icon }, i) => {
              const isActive = activeTab === id;
              return (
                <button
                  key={id}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                    if (id === 'mybux') (forYouTabRef as any).current = el;
                  }}
                  onClick={() => requestTab(id)}
                  className={cn(
                    'relative z-10 flex flex-col items-center gap-0.5 rounded-full transition-colors duration-300',
                    isActive ? 'text-blue-400 px-6 py-1.5' : 'text-white/80 px-6 py-1.5'
                  )}
                >
                  <Icon active={isActive} />
                  <span
                    className={cn(
                      'text-[10px] leading-none transition-colors duration-300',
                      isActive ? 'font-semibold text-blue-400' : 'font-medium text-white/80'
                    )}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Profile avatar button */}
          <button
            data-nav-avatar="true"
            onClick={() => setIsProfileOpen(true)}
            className="relative shrink-0 aspect-square h-full rounded-full flex items-center justify-center transition-transform active:scale-90 overflow-hidden"
            style={{
              boxShadow: '0 6px 6px rgba(0, 0, 0, 0.2), 0 0 20px rgba(0, 0, 0, 0.1)',
              border: '0.5px solid rgba(255, 255, 255, 0.2)',
              pointerEvents: 'auto',
            }}
          >
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                zIndex: 0,
                backdropFilter: 'blur(3px)',
                WebkitBackdropFilter: 'blur(3px)',
                overflow: 'hidden',
                isolation: 'isolate',
              }}
            />
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Profile"
                className="relative z-10 w-[55px] h-[55px] object-cover rounded-full"
              />
            ) : (
              <div className="relative z-10 w-[55px] h-[55px] rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {displayName[0]?.toUpperCase() || '?'}
                </span>
              </div>
            )}
          </button>
          </div>
        </div>
      </div>

      <V2ProfileSheet
        isOpen={isProfileOpen}
        onOpenChange={setIsProfileOpen}
        avatarUrl={avatarUrl ?? null}
        displayName={displayName}
        email={email}
        isKidsProfile={isKidsProfile}
        subscriptionStatus={subscriptionStatus?.subscription_status || null}
        onSwitchProfiles={handleSwitchProfiles}
        onLogout={handleLogout}
      />

      {createPortal(
        <AnimatePresence>
          {isNowPlayingOpen && (currentTrack || currentAudiobookPage) && (
            <motion.div
              key="now-playing"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="fixed inset-0 z-[500]"
            >
              <NowPlaying
                track={currentTrack}
                isPlaying={isPlaying}
                currentTime={currentTime}
                duration={duration}
                isLooping={isLooping}
                onTogglePlay={togglePlay}
                onPrevious={previousTrack}
                onNext={nextTrack}
                onSeek={seek}
                onToggleLoop={toggleLoop}
                onClose={() => setNowPlayingOpen(false)}
              />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
